// DB-05: the bar's entries wrap onto at most two rows; whatever
// does not fit collapses into a "+N more" control.

type FitOptions = {
  /** Horizontal gap between entries (the list's column gap). */
  gap?: number;
  /** Rows the list may take before collapsing. */
  maxRows?: number;
};

/** Rows that `widths` take when wrapped greedily into `available` px. */
const rowsFor = (available: number, widths: number[], gap: number): number => {
  let rows = 0;
  let used = 0;
  widths.forEach((width) => {
    if (rows === 0 || used + gap + width > available) {
      rows += 1;
      used = width;
    } else {
      used += gap + width;
    }
  });
  return rows;
};

/**
 * How many entries to show. All of them when they wrap into `maxRows` rows;
 * otherwise as many as fit with room left for the "+N more" control on the
 * last row. Never fewer than one entry.
 */
export const fitCount = (
  available: number,
  widths: number[],
  moreWidth: number,
  { gap = 16, maxRows = 2 }: FitOptions = {},
): number => {
  if (rowsFor(available, widths, gap) <= maxRows) return widths.length;

  for (let count = widths.length - 1; count > 1; count -= 1) {
    const shown = [...widths.slice(0, count), moreWidth];
    if (rowsFor(available, shown, gap) <= maxRows) return count;
  }
  return 1;
};

/** Set on a bar entry that ends a wrapped row; it then shows no divider. */
export const ROW_END_ATTR = 'data-row-end';

/**
 * Marks each item that is followed, in `list`, by an item on a lower row: the
 * end of a wrapped row (a divider there would point at nothing). The
 * last item is left alone (CSS gives it no divider). Reads offsetTop only, so
 * it is cheap enough to run after every render and resize.
 */
export const markRowEnds = (list: HTMLElement, itemSelector: string) => {
  const items = Array.from(list.querySelectorAll<HTMLElement>(itemSelector));
  items.forEach((item, i) => {
    const next = items[i + 1];
    const ends = next !== undefined && next.offsetTop > item.offsetTop;
    if (ends) item.setAttribute(ROW_END_ATTR, '');
    else item.removeAttribute(ROW_END_ATTR);
  });
};
